import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileText, Loader2, Building2, Users, AlertCircle, CheckCircle2, TrendingUp, Euro, Home, ChevronDown, ChevronUp, FolderPlus, Calendar, Clock, SkipForward, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getUserRole, getUserCommissions } from "@/pages/Login";
import { getRegies } from "@/utils/simulateurUtils";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
const fmtC = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

export default function InjectionPdf() {
  const navigate = useNavigate();
  const role = getUserRole();
  const isRegieOrTelepro = role === "regie" || role === "telepro";
  const isAdminRestricted = role === "admin_restricted" || role === "administratif";
  const hideFinancials = isRegieOrTelepro || isAdminRestricted;
  const [step, setStep] = useState("upload"); // upload | review | simulating | results
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [valorisation, setValorisation] = useState(13);
  const [modeCout, setModeCout] = useState("regie");
  const [simResults, setSimResults] = useState(null);
  const [expandedScenario, setExpandedScenario] = useState(null);
  const [expandedLogement, setExpandedLogement] = useState(null);
  const [creatingDossier, setCreatingDossier] = useState(false);
  const [showRdvFlow, setShowRdvFlow] = useState(false);
  const [rdvCurrentIdx, setRdvCurrentIdx] = useState(0);
  const [rdvData, setRdvData] = useState([]);
  const [rdvSaving, setRdvSaving] = useState(false);
  const [rdvBusySlots, setRdvBusySlots] = useState([]);
  const [rdvAdminName, setRdvAdminName] = useState("");

  // ═══ EDITABLE REVIEW HELPERS ═══
  const updateBeneficiary = (idx, field, value) => {
    setParsedData(prev => {
      const updated = { ...prev, beneficiaries: [...prev.beneficiaries] };
      updated.beneficiaries[idx] = { ...updated.beneficiaries[idx], [field]: value };
      return updated;
    });
  };
  const updateLogement = (buildingIdx, logIdx, field, value) => {
    setParsedData(prev => {
      const updated = { ...prev, buildings: prev.buildings.map((b, bi) => bi === buildingIdx ? { ...b, logements: b.logements.map((l, li) => li === logIdx ? { ...l, [field]: value } : l) } : b) };
      return updated;
    });
  };

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (f && f.type === "application/pdf") setFile(f);
    else toast.error("Veuillez sélectionner un fichier PDF");
  };

  const handleParse = useCallback(async () => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/injection-pdf/parse`, { method: "POST", body: fd });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Erreur"); }
      const data = await res.json();
      setParsedData(data);
      setStep("review");
      if (data.pdf_type === "individual") {
        toast.success(`${data.total_beneficiaries} bénéficiaire(s) extraits`);
      } else {
        toast.success(`${data.total_logements} logements extraits de ${data.total_buildings} immeuble(s)`);
      }
    } catch (err) { toast.error(err.message || "Erreur lors du parsing"); }
    setParsing(false);
  }, [file]);

  const handleSimulate = useCallback(async () => {
    if (!parsedData) return;
    setStep("simulating");
    try {
      const valo = isRegieOrTelepro ? null : valorisation;
      const payload = parsedData.pdf_type === "individual"
        ? { pdf_type: "individual", beneficiaries: parsedData.beneficiaries, valorisation: valo, mode_cout: modeCout }
        : { pdf_type: "sci", buildings: parsedData.buildings, valorisation: valo, mode_cout: modeCout };
      const res = await fetch(`${API}/injection-pdf/simulate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSimResults(data);
      setStep("results");
      toast.success("Simulation terminée !");
    } catch (err) { toast.error("Erreur lors de la simulation"); setStep("review"); }
  }, [parsedData, valorisation, modeCout]);

  const handleCreateDossier = useCallback(async () => {
    if (!parsedData || !simResults) return;
    setCreatingDossier(true);
    try {
      const isIndividualPdf = parsedData.pdf_type === "individual";
      
      if (isIndividualPdf) {
        // INDIVIDUAL: Create 1 dossier per beneficiary
        let created = 0;
        for (const r of simResults.per_logement) {
          const l = r.logement;
          const isCumule = r.eligCumule?.eligible;
          const prog = isCumule ? "cumule" : r.elig174?.eligible ? "174" : "MPR";
          const profil = (l.type_beneficiaire || "").toLowerCase().includes("sci") ? "bailleur" : "proprietaire_occupant";
          const payload = {
            client_name: l.nom_occupant || "", client_phone: l.tel_occupant || "",
            profile: profil, programme: prog, zone: l.zone || "H1", metrage: l.surface || 70,
            precarite: (l.precarite || "bleu").toUpperCase(), adresse_logement: l.building_adresse || "",
            notes: `Injection PDF — ${l.type_logement} ${l.surface}m² ${l.chauffage}`,
            statut_dossier: "rdv_admin", assigned_to: localStorage.getItem("label_energie_user") || "",
            created_by: localStorage.getItem("label_energie_user") || "", nombre_logements: 1,
          };
          const res = await fetch(`${API}/dossiers`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
          if (res.ok) created++;
        }
        toast.success(`${created} dossier(s) créé(s) !`);
        navigate(isRegieOrTelepro ? "/dossiers-regie" : "/dossiers");
      } else {
        // SCI: existing logic
        const sci = parsedData.sci;
      const totalLogements = simResults.total_logements;
      const hasMixed = simResults.has_mixed;
      const bestProg = hasMixed ? "cumule" : (simResults.summary.programme_cumule.marge_totale > 0 ? "cumule" : "174");
      
      // Build logements array with individual steps and sorted by address then name
      const STEPS_174 = [
        { key: "audit_avant_travaux", name: "Audit avant travaux", order: 1 },
        { key: "devis_signe", name: "Devis signé", order: 2 },
        { key: "planification_locataires", name: "Planification locataires", order: 3 },
        { key: "date_demarrage_travaux", name: "Date démarrage travaux", order: 4 },
        { key: "travaux_realises", name: "Travaux réalisés", order: 5 },
        { key: "cofrac", name: "COFRAC", order: 6 },
        { key: "signature_doc_clients", name: "Signature Doc clients", order: 7 },
        { key: "depot", name: "Dépôt", order: 8 },
        { key: "paye", name: "Payé", order: 9 },
        { key: "sav_174", name: "SAV", order: 10 },
      ];
      const STEPS_MPR = [
        { key: "mpr_devis_signe", name: "Devis signé MPR", order: 1, programme: "mpr" },
        { key: "compte_mpr", name: "Compte MPR", order: 2, programme: "mpr" },
        { key: "dossier_valide_ynergie", name: "Dossier validé Ynergie", order: 3, programme: "mpr" },
        { key: "dossier_octroye", name: "Dossier octroyé", order: 4, programme: "mpr" },
        { key: "dossier_a_installer", name: "Travaux à réaliser MPR", order: 5, programme: "mpr" },
        { key: "dossier_installe", name: "Travaux terminés MPR", order: 6, programme: "mpr" },
        { key: "prime_cee", name: "Prime CEE encaissée", order: 7, programme: "mpr" },
        { key: "prime_apa", name: "APA encaissée", order: 8, programme: "mpr" },
        { key: "prime_mpr", name: "MPR encaissée", order: 9, programme: "mpr" },
        { key: "sav_mpr", name: "SAV MPR", order: 10, programme: "mpr" },
      ];

      const logements = simResults.per_logement.map((r, idx) => {
        const l = r.logement;
        const isCumule = r.eligCumule?.eligible;
        const is174Only = r.elig174?.eligible && !r.eligMPR?.eligible;
        let steps;
        let logProgramme;
        if (isCumule) {
          steps = [...STEPS_174.map(s => ({...s, programme: "174"})), ...STEPS_MPR];
          logProgramme = "cumule";
        } else {
          steps = STEPS_174.map(s => ({...s}));
          logProgramme = "174";
        }
        steps = steps.map(s => ({ ...s, status: "not_started", updated_at: null, comment: null, amount: null }));
        
        // Best scenario travaux detail
        const bestResult = r.results174?.[0] || r.resultsCumule174?.[0];
        
        return {
          id: `log-${idx + 1}`,
          num: l.num,
          nom_occupant: l.nom_occupant || "",
          tel_occupant: l.tel_occupant || "",
          building_adresse: l.building_adresse || "",
          building_cp: l.building_cp || l.zone || "",
          zone: l.zone || "H1",
          surface: l.surface || 70,
          type_logement: l.type_logement || "maison",
          chauffage: l.chauffage || "Gaz",
          dpe: l.dpe || "G",
          precarite: l.precarite || "bleu",
          age: l.age || "more_than_15",
          gestes_deja: l.gestes_deja || [],
          programme: logProgramme,
          steps,
          steps_progress: 0,
          travaux_detail: bestResult?.result?.travaux_detail || [],
          simulation: {
            caTTC: bestResult?.result?.caTTC || 0,
            marge: bestResult?.result?.marge || 0,
            scenario: bestResult?.scenario?.label || "",
          },
        };
      });

      // Sort by building address then occupant name
      logements.sort((a, b) => {
        const addrCmp = (a.building_adresse || "").localeCompare(b.building_adresse || "");
        if (addrCmp !== 0) return addrCmp;
        return (a.nom_occupant || "").localeCompare(b.nom_occupant || "");
      });

      // Build global steps (union of all unique step keys)
      const globalStepKeys = new Set();
      const globalSteps = [];
      for (const log of logements) {
        for (const s of log.steps) {
          if (!globalStepKeys.has(s.key)) {
            globalStepKeys.add(s.key);
            globalSteps.push({ key: s.key, name: s.name, order: globalSteps.length + 1, status: "not_started", programme: s.programme });
          }
        }
      }

      const optimalMarge = hasMixed ? simResults.optimal.box_combined.marge_totale : (simResults.summary.programme_cumule.marge_totale || simResults.summary.programme_174.marge_totale);
      const optimalCA = hasMixed ? simResults.optimal.box_combined.ca_total : (simResults.summary.programme_cumule.ca_total || simResults.summary.programme_174.ca_total);

      const dossierPayload = {
        client_name: sci.nom_sci || "SCI",
        client_email: sci.email || "",
        client_phone: sci.tel_contact || "",
        profile: "bailleur",
        programme: bestProg,
        zone: parsedData.buildings?.[0]?.zone || "H1",
        metrage: 0,
        precarite: "BLEU",
        code_postal: sci.code_postal_sci || "",
        adresse_logement: sci.adresse_sci || "",
        notes: `Injection PDF — ${totalLogements} logements, ${parsedData.total_buildings} immeuble(s). Contact: ${sci.nom_contact} (${sci.tel_contact})`,
        statut_dossier: "rdv_admin",
        assigned_to: localStorage.getItem("label_energie_user") || "",
        created_by: localStorage.getItem("label_energie_user") || "",
        nombre_logements: totalLogements,
        organisme_bailleur: sci.nom_sci,
        nom_representant: sci.nom_contact,
        tel_representant: sci.tel_contact,
        email_representant: sci.email,
        adresse_bailleur: sci.adresse_sci,
        sim_marge_prevue: optimalMarge,
        sim_total_aides: optimalCA,
        is_sci_multi: true,
        sci_info: sci,
        logements,
      };
      const res = await fetch(`${API}/dossiers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dossierPayload),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success("Dossier SCI créé avec succès !");
      navigate(isRegieOrTelepro ? `/dossier-regie/${data.id}` : `/dossiers/${data.id}`);
      }
    } catch (err) { console.error(err); toast.error("Erreur lors de la création"); }
    setCreatingDossier(false);
  }, [parsedData, simResults, navigate, isRegieOrTelepro]);

  // ═══ UPLOAD STEP ═══
  if (step === "upload") {
    return (
      <div className="p-6 md:p-8 space-y-6 max-w-3xl mx-auto" data-testid="injection-pdf-page">
        <div>
          <h1 className="text-2xl font-heading font-bold">Injection PDF — Simulation SCI</h1>
          <p className="text-sm text-muted-foreground mt-1">Importez votre fichier PDF contenant les données des logements pour lancer une simulation automatique</p>
        </div>
        <div className="border-2 border-dashed border-border rounded-xl p-12 text-center bg-card hover:border-emerald-500/40 transition-colors">
          <Upload className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
          <p className="font-semibold mb-2">Glissez votre PDF ici ou cliquez pour sélectionner</p>
          <p className="text-xs text-muted-foreground mb-4">Format attendu : PDF avec tableau SCI (bénéficiaire + immeubles + logements)</p>
          <input type="file" accept=".pdf" onChange={handleFilePick} className="hidden" id="pdf-input" />
          <label htmlFor="pdf-input" className="inline-block px-6 py-2.5 rounded-lg bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green-hover))] text-white font-semibold text-sm cursor-pointer shadow" data-testid="btn-select-pdf">
            Choisir un fichier PDF
          </label>
          {file && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[hsl(var(--renova-green))]" />
              <span className="font-medium">{file.name}</span>
              <span className="text-muted-foreground">({Math.round(file.size / 1024)} Ko)</span>
            </div>
          )}
        </div>
        {file && (
          <Button onClick={handleParse} disabled={parsing} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base" data-testid="btn-parse-pdf">
            {parsing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Analyse en cours...</> : <><FileText className="w-5 h-5 mr-2" />Analyser le PDF</>}
          </Button>
        )}
      </div>
    );
  }

  // ═══ REVIEW STEP ═══
  if (step === "review" && parsedData) {
    const isIndividual = parsedData.pdf_type === "individual";
    const totalCount = isIndividual ? parsedData.total_beneficiaries : parsedData.total_logements;
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="injection-pdf-review">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Données extraites {!isIndividual && `— ${parsedData.sci?.nom_sci}`}</h1>
            <p className="text-sm text-muted-foreground mt-1">{totalCount} {isIndividual ? "bénéficiaire(s)" : `logements dans ${parsedData.total_buildings} immeuble(s)`}</p>
          </div>
          <Button variant="outline" onClick={() => { setStep("upload"); setFile(null); setParsedData(null); }} className="rounded-xl">Nouveau PDF</Button>
        </div>

        {/* SCI Info — only for SCI type */}
        {!isIndividual && parsedData.sci && (
        <div className="border border-border rounded-xl bg-card p-5">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><Building2 className="w-4 h-4 text-blue-500" />Informations SCI</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div><span className="text-xs text-muted-foreground">Nom SCI</span><p className="font-semibold">{parsedData.sci.nom_sci || "—"}</p></div>
            <div><span className="text-xs text-muted-foreground">Contact</span><p className="font-semibold">{parsedData.sci.nom_contact || "—"}</p></div>
            <div><span className="text-xs text-muted-foreground">Téléphone</span><p className="font-semibold">{parsedData.sci.tel_contact || "—"}</p></div>
            <div><span className="text-xs text-muted-foreground">Adresse</span><p className="font-semibold">{parsedData.sci.adresse_sci || "—"}</p></div>
            <div><span className="text-xs text-muted-foreground">Code postal</span><p className="font-semibold">{parsedData.sci.code_postal_sci || "—"}</p></div>
            <div><span className="text-xs text-muted-foreground">Email</span><p className="font-semibold">{parsedData.sci.email || "—"}</p></div>
          </div>
        </div>
        )}

        {/* Individual beneficiaries table — EDITABLE */}
        {isIndividual && (
        <div className="border border-border rounded-xl bg-card overflow-hidden">
          <div className="p-4 bg-muted/30 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[hsl(var(--renova-green))]" />
              <span className="font-semibold text-sm">{parsedData.total_beneficiaries} bénéficiaire(s)</span>
            </div>
            <span className="text-[10px] text-muted-foreground">Modifiez les cellules avant de lancer la simulation</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b-2 border-border bg-muted/40">
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Nom</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Type</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Tél.</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Adresse</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">CP</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Zone</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Logement</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Surface</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Chauffage</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">DPE</th>
                <th className="px-2 py-2.5 text-left font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Précarité</th>
              </tr></thead>
              <tbody>
                {parsedData.beneficiaries.map((b, bi) => (
                  <tr key={bi} className={cn("border-b border-border/30", bi % 2 === 0 ? "bg-card" : "bg-muted/10")}>
                    <td className="px-1 py-1.5"><input className="w-full px-2 py-1 text-xs border border-border/50 rounded-sm bg-card focus:ring-1 focus:ring-emerald-300 focus:border-emerald-500/40 outline-none" value={b.nom || ""} onChange={e => updateBeneficiary(bi, "nom", e.target.value)} /></td>
                    <td className="px-1 py-1.5"><select className="w-full px-1 py-1 text-xs border border-border/50 rounded-sm bg-card" value={b.type_beneficiaire || ""} onChange={e => updateBeneficiary(bi, "type_beneficiaire", e.target.value)}><option value="SCI">SCI</option><option value="Propriétaire Occupant">Prop. Occupant</option></select></td>
                    <td className="px-1 py-1.5"><input className="w-full px-2 py-1 text-xs border border-border/50 rounded-sm bg-card focus:ring-1 focus:ring-emerald-300 outline-none" value={b.telephone || ""} onChange={e => updateBeneficiary(bi, "telephone", e.target.value)} /></td>
                    <td className="px-1 py-1.5"><input className="w-full px-2 py-1 text-xs border border-border/50 rounded-sm bg-card focus:ring-1 focus:ring-emerald-300 outline-none" value={b.adresse || ""} onChange={e => updateBeneficiary(bi, "adresse", e.target.value)} /></td>
                    <td className="px-1 py-1.5"><input className="w-20 px-2 py-1 text-xs border border-border/50 rounded-sm bg-card focus:ring-1 focus:ring-emerald-300 outline-none" value={b.code_postal || ""} onChange={e => updateBeneficiary(bi, "code_postal", e.target.value)} /></td>
                    <td className="px-1 py-1.5"><span className="text-xs font-mono font-bold text-blue-600">{b.zone || "H1"}</span></td>
                    <td className="px-1 py-1.5"><select className="w-full px-1 py-1 text-xs border border-border/50 rounded-sm bg-card" value={b.type_logement || ""} onChange={e => updateBeneficiary(bi, "type_logement", e.target.value)}><option value="maison">Maison</option><option value="appartement">Appt.</option></select></td>
                    <td className="px-1 py-1.5"><input type="number" className="w-16 px-2 py-1 text-xs border border-border/50 rounded-sm bg-card text-right focus:ring-1 focus:ring-emerald-300 outline-none" value={b.surface || ""} onChange={e => updateBeneficiary(bi, "surface", parseFloat(e.target.value) || 0)} /></td>
                    <td className="px-1 py-1.5"><select className="w-full px-1 py-1 text-xs border border-border/50 rounded-sm bg-card" value={b.chauffage || ""} onChange={e => updateBeneficiary(bi, "chauffage", e.target.value)}><option value="Gaz">Gaz</option><option value="Fioul">Fioul</option><option value="Electrique">Élec.</option><option value="Bois">Bois</option></select></td>
                    <td className="px-1 py-1.5"><select className="w-14 px-1 py-1 text-xs border border-border/50 rounded-sm bg-card" value={b.dpe || ""} onChange={e => updateBeneficiary(bi, "dpe", e.target.value)}><option value="">—</option>{["A","B","C","D","E","F","G"].map(d => <option key={d} value={d}>{d}</option>)}</select></td>
                    <td className="px-1 py-1.5"><select className="w-full px-1 py-1 text-xs border border-border/50 rounded-sm bg-card" value={b.precarite || ""} onChange={e => updateBeneficiary(bi, "precarite", e.target.value)}><option value="bleu">Bleu</option><option value="jaune">Jaune</option></select></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Buildings & Logements — SCI only — EDITABLE */}
        {!isIndividual && parsedData.buildings?.map((b, bi) => (
          <div key={bi} className="border border-border rounded-xl bg-card overflow-hidden">
            <div className="p-4 bg-muted/30 border-b border-border flex items-center gap-3">
              <Home className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="font-semibold text-sm">{b.adresse}</p>
                <p className="text-xs text-muted-foreground">CP {b.code_postal} — Zone {b.zone} — {b.logements.length} logement(s) — cliquez pour modifier</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-border bg-muted/20">
                  <th className="px-2 py-2 text-left font-semibold">N°</th>
                  <th className="px-2 py-2 text-left font-semibold">Occupant</th>
                  <th className="px-2 py-2 text-left font-semibold">Tél.</th>
                  <th className="px-2 py-2 text-left font-semibold">Type</th>
                  <th className="px-2 py-2 text-left font-semibold">Surface</th>
                  <th className="px-2 py-2 text-left font-semibold">Chauffage</th>
                  <th className="px-2 py-2 text-left font-semibold">DPE</th>
                  <th className="px-2 py-2 text-left font-semibold">Précarité</th>
                </tr></thead>
                <tbody>
                  {b.logements.map((l, li) => (
                    <tr key={li} className="border-b border-border/30">
                      <td className="px-2 py-1 font-mono font-bold">{l.num}</td>
                      <td className="px-1 py-1"><input className="w-full px-1 py-0.5 text-xs border border-border/50 rounded bg-card" value={l.nom_occupant || ""} onChange={e => updateLogement(bi, li, "nom_occupant", e.target.value)} /></td>
                      <td className="px-1 py-1"><input className="w-full px-1 py-0.5 text-xs border border-border/50 rounded bg-card" value={l.tel_occupant || ""} onChange={e => updateLogement(bi, li, "tel_occupant", e.target.value)} /></td>
                      <td className="px-1 py-1"><select className="w-full px-1 py-0.5 text-xs border border-border/50 rounded bg-card" value={l.type_logement || ""} onChange={e => updateLogement(bi, li, "type_logement", e.target.value)}><option value="maison">Maison</option><option value="appartement">Appt.</option></select></td>
                      <td className="px-1 py-1"><input type="number" className="w-16 px-1 py-0.5 text-xs border border-border/50 rounded bg-card" value={l.surface || ""} onChange={e => updateLogement(bi, li, "surface", parseFloat(e.target.value) || 0)} /></td>
                      <td className="px-1 py-1"><select className="w-full px-1 py-0.5 text-xs border border-border/50 rounded bg-card" value={l.chauffage || ""} onChange={e => updateLogement(bi, li, "chauffage", e.target.value)}><option value="Gaz">Gaz</option><option value="Fioul">Fioul</option><option value="Electrique">Élec.</option><option value="Bois">Bois</option></select></td>
                      <td className="px-1 py-1"><select className="w-14 px-1 py-0.5 text-xs border border-border/50 rounded bg-card" value={l.dpe || ""} onChange={e => updateLogement(bi, li, "dpe", e.target.value)}><option value="">—</option>{["A","B","C","D","E","F","G"].map(d => <option key={d} value={d}>{d}</option>)}</select></td>
                      <td className="px-1 py-1"><select className="w-full px-1 py-0.5 text-xs border border-border/50 rounded bg-card" value={l.precarite || ""} onChange={e => updateLogement(bi, li, "precarite", e.target.value)}><option value="bleu">Bleu</option><option value="jaune">Jaune</option></select></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* Simulation parameters — admin only */}
        {!hideFinancials && (
        <div className="border border-border rounded-xl bg-card p-5">
          <h3 className="font-semibold text-sm mb-3">Paramètres du calcul</h3>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="text-xs font-semibold">Valorisation CEE 174 (€/MWh)</label>
              <Input type="number" value={valorisation} onChange={e => setValorisation(parseFloat(e.target.value) || 13)} className="w-24 mt-1 rounded-sm" data-testid="input-valorisation" />
            </div>
            <div className="text-xs text-muted-foreground py-2">
              Commission Régie : <strong>10% HT</strong> du CA Total HT optimal (TVA 5,5%)
            </div>
          </div>
        </div>
        )}

        <Button onClick={handleSimulate} className="w-full rounded-xl bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green-hover))] text-white py-6 text-base" data-testid="btn-launch-simulation">
          <TrendingUp className="w-5 h-5 mr-2" />Lancer la simulation pour {totalCount} {isIndividual ? "bénéficiaire(s)" : "logements"}
        </Button>
      </div>
    );
  }

  // ═══ SIMULATING ═══
  if (step === "simulating") {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[hsl(var(--renova-green))] mx-auto mb-4" />
          <p className="font-semibold text-lg">Simulation en cours...</p>
          <p className="text-sm text-muted-foreground mt-1">Calcul individuel pour chaque logement</p>
        </div>
      </div>
    );
  }

  // ═══ RESULTS STEP ═══
  if (step === "results" && simResults) {
    const s = simResults.summary;
    const best = s.programme_cumule.marge_totale >= s.programme_174.marge_totale && s.programme_cumule.marge_totale >= s.programme_mpr.marge_totale
      ? "cumule" : s.programme_174.marge_totale >= s.programme_mpr.marge_totale ? "174" : "mpr";

    const isIndividualResults = parsedData?.pdf_type === "individual";

    // RDV flow handler
    const startRdvFlow = async () => {
      // Fetch assigned admin
      const currentUser = localStorage.getItem("label_energie_user") || "";
      try {
        const aRes = await fetch(`${API}/users/assigned-admin/${encodeURIComponent(currentUser)}`);
        const aData = await aRes.json();
        setRdvAdminName(aData.admins?.[0]?.username || "Admin");
      } catch (err) { setRdvAdminName("Admin"); }
      
      const benefList = simResults.per_logement.map(r => ({
        nom: r.logement.nom_occupant || "",
        telephone: r.logement.tel_occupant || "",
        adresse: r.logement.building_adresse || "",
        programme: r.eligCumule?.eligible ? "Cumulée" : r.elig174?.eligible ? "174" : "MPR",
        date: "", heure: "", attribue_a: "", skipped: false,
      }));
      setRdvData(benefList);
      setRdvCurrentIdx(0);
      setRdvBusySlots([]);
      setShowRdvFlow(true);
    };

    const handleRdvNext = () => {
      if (rdvCurrentIdx < rdvData.length - 1) setRdvCurrentIdx(rdvCurrentIdx + 1);
      else handleRdvFinish();
    };
    const handleRdvSkip = () => {
      setRdvData(prev => prev.map((r, i) => i === rdvCurrentIdx ? { ...r, skipped: true } : r));
      handleRdvNext();
    };
    const handleRdvFinish = async () => {
      setRdvSaving(true);
      try {
        let created = 0;
        const currentUser = localStorage.getItem("label_energie_user") || "";
        const adminName = rdvAdminName || "Admin";
        
        for (let i = 0; i < rdvData.length; i++) {
          const rdv = rdvData[i];
          const r = simResults.per_logement[i];
          const l = r.logement;
          const isCumule = r.eligCumule?.eligible;
          const prog = isCumule ? "cumule" : r.elig174?.eligible ? "174" : "MPR";
          const profil = (l.type_beneficiaire || "").toLowerCase().includes("sci") ? "bailleur" : "proprietaire_occupant";
          
          // Create dossier
          const dossierRes = await fetch(`${API}/dossiers`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              client_name: l.nom_occupant || "", client_phone: l.tel_occupant || "",
              profile: profil, programme: prog, zone: l.zone || "H1", metrage: l.surface || 70,
              precarite: (l.precarite || "bleu").toUpperCase(), adresse_logement: l.building_adresse || "",
              notes: rdv.skipped ? "Injection PDF — Pas de RDV planifié" : `RDV Admin le ${rdv.date} à ${rdv.heure}`,
              statut_dossier: "rdv_admin", assigned_to: currentUser, created_by: currentUser, nombre_logements: 1,
            }),
          });
          let dossierId = null;
          if (dossierRes.ok) { const dd = await dossierRes.json(); dossierId = dd.id; created++; }

          // Create RDV if not skipped and date is set
          if (!rdv.skipped && rdv.date && rdv.heure && dossierId) {
            await fetch(`${API}/planning/rdvs`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                date: rdv.date, heure: rdv.heure,
                attribue_a: rdv.attribue_a || adminName,
                client_nom: l.nom_occupant, client_telephone: l.tel_occupant,
                client_adresse: l.building_adresse, programme: rdv.programme,
                notes: "", created_by: currentUser, rdv_type: "rdv_admin", dossier_id: dossierId,
              }),
            });
          }
        }
        toast.success(`${created} dossier(s) et RDV créés !`);
        setShowRdvFlow(false);
        navigate(isRegieOrTelepro ? "/dossiers-regie" : "/dossiers");
      } catch (err) { toast.error("Erreur"); }
      setRdvSaving(false);
    };

    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="injection-pdf-results">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">{parsedData.sci?.nom_sci || "Injection PDF"} — Résultats</h1>
            <p className="text-sm text-muted-foreground mt-1">{simResults.total_logements} {isIndividualResults ? "bénéficiaire(s)" : "logements"} analysés</p>
          </div>
          <div className="flex gap-2">
            {isIndividualResults && (
              <Button onClick={startRdvFlow} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-planif-rdv-batch">
                <Calendar className="w-4 h-4 mr-2" />Planifier les RDV Admin
              </Button>
            )}
            <Button onClick={handleCreateDossier} disabled={creatingDossier} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-create-dossier-sci">
              {creatingDossier ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-2" />}
              {creatingDossier ? "Création..." : isIndividualResults ? "Créer les Dossiers" : "Créer le dossier SCI"}
            </Button>
            <Button variant="outline" onClick={() => setStep("review")} className="rounded-xl">Modifier</Button>
            <Button variant="outline" onClick={async () => {
              try {
                toast.info("Export PDF en cours...");
                const exportData = { ...simResults, title: parsedData.sci?.nom_sci || "Simulation PDF", is_regie: isRegieOrTelepro };
                const res = await fetch(`${API}/export/simulation-results`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(exportData) });
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = `Simulation_Renova_${new Date().toISOString().slice(0,10)}.pdf`; a.click(); window.URL.revokeObjectURL(url);
                toast.success("PDF téléchargé");
              } catch (err) { toast.error("Erreur export"); }
            }} className="rounded-xl"><Download className="w-4 h-4 mr-1" />PDF</Button>
          </div>
        </div>

        {/* Parameters bar */}
        <div className="border border-border rounded-xl bg-card p-4 flex items-center gap-4 flex-wrap text-sm">
          {!hideFinancials && <span className="text-xs text-muted-foreground">Valorisation CEE 174 : <strong>{valorisation} €/MWh</strong></span>}
          {!hideFinancials && !isIndividualResults && <span className="px-3 py-1 rounded-lg text-xs font-bold border bg-emerald-500/10 text-emerald-700 border-emerald-500/30">SCI — 10% HT</span>}
          {!hideFinancials && isIndividualResults && <span className="px-3 py-1 rounded-lg text-xs font-bold border bg-emerald-500/10 text-emerald-700 border-emerald-500/30">Commission par bénéficiaire</span>}
          <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-bold">{simResults.total_logements} logements — Totaux affichés</span>
        </div>

        {/* Recommendation cards — Optimal combination */}
        <div>
          <h2 className="font-heading font-bold text-base flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 text-[hsl(var(--renova-green))]" />
            Recommandation
          </h2>
          {simResults.has_mixed ? (
            /* MIXED CASE: some cumule + some 174-only → show optimal combination */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Box 1: Op. Cumulée for eligible logements */}
              <div className="border-2 border-[hsl(var(--renova-green))] rounded-xl p-5 bg-card shadow-lg" data-testid="card-cumule">
                <p className="text-[10px] font-bold text-[hsl(var(--renova-green))] uppercase mb-1">Recommandé</p>
                <p className="font-bold text-sm mb-3">Op. Cumulée (174 + MPR)</p>
                <div className="space-y-1">
                  {!isRegieOrTelepro && <div className="flex justify-between"><span className="text-xs text-muted-foreground">CA Total</span><span className="font-bold">{fmtC(simResults.optimal.box_cumule.ca_total)}</span></div>}
                  {!hideFinancials && <div className="flex justify-between"><span className="text-xs text-muted-foreground">Marge Totale</span><span className="font-bold text-emerald-600">{fmtC(simResults.optimal.box_cumule.marge_totale)}</span></div>}
                </div>
                <div className={cn("pt-3 border-t border-border/50", !isRegieOrTelepro && "mt-3")}>
                  <span className="text-[10px] text-muted-foreground">{simResults.optimal.box_cumule.count} logement(s) — maisons</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--renova-green))]" />
              </div>
              {/* Box 2: 174 seul for remaining logements */}
              <div className="border-2 border-emerald-400 rounded-xl p-5 bg-card shadow-lg" data-testid="card-174-only">
                <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Recommandé</p>
                <p className="font-bold text-sm mb-3">Programme 174 seul</p>
                <div className="space-y-1">
                  {!isRegieOrTelepro && <div className="flex justify-between"><span className="text-xs text-muted-foreground">CA Total</span><span className="font-bold">{fmtC(simResults.optimal.box_174_only.ca_total)}</span></div>}
                  {!hideFinancials && <div className="flex justify-between"><span className="text-xs text-muted-foreground">Marge Totale</span><span className="font-bold text-emerald-600">{fmtC(simResults.optimal.box_174_only.marge_totale)}</span></div>}
                </div>
                <div className={cn("pt-3 border-t border-border/50", !isRegieOrTelepro && "mt-3")}>
                  <span className="text-[10px] text-muted-foreground">{simResults.optimal.box_174_only.count} logement(s) — appartements</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-emerald-400" />
              </div>
              {/* Box 3: Total combiné */}
              <div className="border-2 border-violet-400 rounded-xl p-5 bg-violet-500/10/30 shadow-lg" data-testid="card-combined">
                <p className="text-[10px] font-bold text-violet-600 uppercase mb-1">Total Optimal</p>
                <p className="font-bold text-sm mb-3">Cumulée + 174</p>
                <div className="space-y-1">
                  {!isRegieOrTelepro && <div className="flex justify-between"><span className="text-xs text-muted-foreground">CA Total</span><span className="font-bold text-lg">{fmtC(simResults.optimal.box_combined.ca_total)}</span></div>}
                  {!hideFinancials && <div className="flex justify-between"><span className="text-xs text-muted-foreground">Marge Totale</span><span className="font-bold text-emerald-600 text-lg">{fmtC(simResults.optimal.box_combined.marge_totale)}</span></div>}
                </div>
                <div className={cn("pt-3 border-t border-border/50", !isRegieOrTelepro && "mt-3")}>
                  <span className="text-[10px] text-muted-foreground">{simResults.optimal.box_combined.count} logement(s) total</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-violet-500" />
              </div>
            </div>
          ) : (
            /* SIMPLE CASE: all same eligibility → show classic 3 boxes */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: "cumule", label: "Op. Cumulée (174 puis MPR)", data: s.programme_cumule },
                { key: "174", label: "Programme 174", data: s.programme_174 },
                { key: "mpr", label: `Programme MPR (${s.programme_mpr.best_type || "PAC/SSC"})`, data: s.programme_mpr },
              ].map(p => (
                <div key={p.key} className={cn("border-2 rounded-xl p-5 bg-card transition-shadow",
                  best === p.key ? "border-[hsl(var(--renova-green))] shadow-lg" : "border-border"
                )} data-testid={`card-${p.key}`}>
                  {best === p.key && <p className="text-[10px] font-bold text-[hsl(var(--renova-green))] uppercase mb-1">Recommandé</p>}
                  <p className="font-bold text-sm mb-3">{p.label}</p>
                  <div className="space-y-1">
                    {!isRegieOrTelepro && <div className="flex justify-between"><span className="text-xs text-muted-foreground">CA Total</span><span className="font-bold">{fmtC(p.data.ca_total)}</span></div>}
                    {!hideFinancials && <div className="flex justify-between"><span className="text-xs text-muted-foreground">Marge Totale</span><span className="font-bold text-emerald-600">{fmtC(p.data.marge_totale)}</span></div>}
                  </div>
                  <div className={cn("pt-3 border-t border-border/50", !isRegieOrTelepro && "mt-3")}>
                    <span className="text-[10px] text-muted-foreground">{p.data.count} logement(s) éligible(s)</span>
                  </div>
                  {best === p.key && <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--renova-green))]" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Eligibility cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-4 rounded-xl border bg-emerald-500/10 border-emerald-500/30">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-1" />
            <p className="font-semibold text-sm">Programme 174</p>
            <p className="text-xs text-emerald-700">Bailleur SCI : toujours éligible à la 174. ({simResults.eligible_174_count}/{simResults.total_logements})</p>
          </div>
          <div className={cn("p-4 rounded-xl border", simResults.eligible_mpr_count > 0 ? "bg-blue-500/10 border-blue-500/30" : "bg-red-500/10 border-red-500/30")}>
            {simResults.eligible_mpr_count > 0 ? <CheckCircle2 className="w-5 h-5 text-blue-600 mb-1" /> : <AlertCircle className="w-5 h-5 text-red-600 mb-1" />}
            <p className="font-semibold text-sm">Programme MPR</p>
            <p className="text-xs">{simResults.eligible_mpr_count}/{simResults.total_logements} éligible(s)</p>
          </div>
          <div className={cn("p-4 rounded-xl border", simResults.eligible_cumule_count > 0 ? "bg-violet-500/10 border-violet-500/30" : "bg-red-500/10 border-red-500/30")}>
            {simResults.eligible_cumule_count > 0 ? <CheckCircle2 className="w-5 h-5 text-violet-600 mb-1" /> : <AlertCircle className="w-5 h-5 text-red-600 mb-1" />}
            <p className="font-semibold text-sm">Op. Cumulée</p>
            <p className="text-xs">{simResults.eligible_cumule_count}/{simResults.total_logements} éligible(s)</p>
          </div>
        </div>

        {/* Non-eligible logements */}
        {simResults.non_eligible?.length > 0 && (
          <div className="border-2 border-red-500/30 rounded-xl bg-red-500/10 p-4">
            <h3 className="font-semibold text-sm text-red-700 flex items-center gap-2 mb-2"><AlertCircle className="w-4 h-4" />Logements non éligibles 174</h3>
            <div className="space-y-1">
              {simResults.non_eligible.map((ne, i) => (
                <p key={i} className="text-xs text-red-600">Logement {ne.num} ({ne.nom}) — {ne.building} : {ne.reason_174}</p>
              ))}
            </div>
          </div>
        )}

        {/* Commission Régie */}
        {(() => {
          const isIndvl = parsedData?.pdf_type === "individual";
          if (isIndvl) {
            // Individual model: sum of commissions per beneficiary from user's configured rates
            const userComm = getUserCommissions();
            let totalComm = 0;
            simResults.per_logement.forEach(r => {
              const l = r.logement;
              const surface = l.surface || 80;
              const prec = (l.precarite || "bleu").toLowerCase();
              const prog = (r.eligCumule?.eligible ? "cumule" : r.elig174?.eligible ? "174" : "mpr").toLowerCase();
              const regies = getRegies("regie", prec === "jaune" ? "jaune" : "bleu", surface, userComm);
              if (prog === "cumule") totalComm += (regies.r174Cum || 0) + (regies.rMPRCum || 0);
              else if (prog === "174") totalComm += regies.r174 || 0;
              else totalComm += regies.rMPR || 0;
            });
            return (
              <div className="border-2 border-[hsl(var(--renova-green))]/30 rounded-xl bg-emerald-500/10 p-5" data-testid="commission-regie">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-sm flex items-center gap-2">
                      <Euro className="w-5 h-5 text-[hsl(var(--renova-green))]" />
                      Commission Régie
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">Commission configurée par bénéficiaire</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-extrabold text-[hsl(var(--renova-green))]">{fmtC(totalComm)}</p>
                    <p className="text-xs text-muted-foreground">{simResults.total_logements} bénéficiaire(s)</p>
                  </div>
                </div>
              </div>
            );
          } else {
            // SCI model: X% HT based on user's commission_sci setting
            const optimalCA = simResults.has_mixed 
              ? simResults.optimal.box_combined.ca_total 
              : (best === "cumule" ? s.programme_cumule.ca_total : best === "174" ? s.programme_174.ca_total : s.programme_mpr.ca_total);
            const caHT = optimalCA / 1.055;
            const userCommStr = localStorage.getItem("label_energie_commissions");
            let sciPct = 10;
            if (userCommStr) { try { sciPct = JSON.parse(userCommStr).commission_sci || 10; } catch (err) {} }
            const commissionRegie = caHT * sciPct / 100;
            return (
              <div className="border-2 border-[hsl(var(--renova-green))]/30 rounded-xl bg-emerald-500/10 p-5" data-testid="commission-regie">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-sm flex items-center gap-2">
                      <Euro className="w-5 h-5 text-[hsl(var(--renova-green))]" />
                      Commission Régie
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">{(() => { const uc = localStorage.getItem("label_energie_commissions"); let p = 10; if (uc) { try { p = JSON.parse(uc).commission_sci || 10; } catch (err) {} } return `${p}% HT du CA Total HT optimal (TVA 5,5%)`; })()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-extrabold text-[hsl(var(--renova-green))]">{fmtC(commissionRegie)}</p>
                    {!hideFinancials && <p className="text-xs text-muted-foreground">CA HT : {fmtC(caHT)}</p>}
                  </div>
                </div>
              </div>
            );
          }
        })()}

        {/* Per logement details */}
        <div className="space-y-3">
          <h2 className="font-heading font-bold text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-violet-600" />
            Détail par logement
          </h2>
          {simResults.per_logement.map((r, i) => {
            const l = r.logement;
            const isExpanded = expandedLogement === i;
            const best174 = r.results174?.[0];
            const bestMPR = r.resultsMPR?.sort((a, b) => b.result.marge - a.result.marge)?.[0];
            return (
              <div key={i} className="border border-border rounded-xl bg-card overflow-hidden">
                <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-muted/30" onClick={() => setExpandedLogement(isExpanded ? null : i)} data-testid={`logement-detail-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold">{l.num}</span>
                    <div>
                      <p className="text-sm font-semibold">{l.nom_occupant} — {l.building_adresse}</p>
                      <p className="text-[10px] text-muted-foreground">{l.surface}m² · {l.type_logement} · {l.chauffage} · DPE {l.dpe} · {l.precarite}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {!hideFinancials && r.elig174.eligible && best174 && <span className="text-xs"><span className="text-muted-foreground">174:</span> <span className="font-bold text-emerald-600">{fmtC(best174.result.marge)}</span></span>}
                    {!hideFinancials && r.eligMPR.eligible && bestMPR && <span className="text-xs"><span className="text-muted-foreground">MPR:</span> <span className="font-bold text-blue-600">{fmtC(bestMPR.result.marge)}</span></span>}
                    {isRegieOrTelepro && r.elig174.eligible && <span className="text-xs text-emerald-600 font-bold">174</span>}
                    {isRegieOrTelepro && r.eligMPR.eligible && <span className="text-xs text-blue-600 font-bold">MPR</span>}
                    {!r.elig174.eligible && !r.eligMPR.eligible && <span className="text-xs text-red-600 font-bold">Non éligible</span>}
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-border">
                    {r.elig174.eligible && r.results174.length > 0 && r.results174.map((s, si) => (
                      <div key={si} className={cn("p-4 border-b border-border/50", si === 0 && "bg-emerald-500/10/30")}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-xs font-bold text-emerald-700">{si === 0 ? "174 — Recommandé" : "174 — Scénario"}</p>
                            <p className="text-sm font-semibold">{s.scenario.label} ({s.scenario.sauts} sauts)</p>
                            <p className="text-[10px] text-muted-foreground">{(s.scenario.gestes || []).join(", ")}{(s.scenario.equipements || []).length > 0 ? ` + ${s.scenario.equipements.join(", ")}` : ""}</p>
                          </div>
                          {!hideFinancials && <span className="font-bold text-emerald-600">{fmtC(s.result.marge)}</span>}
                        </div>
                        {/* Travaux detail table — admin only */}
                        {!hideFinancials && s.result.travaux_detail && s.result.travaux_detail.length > 0 && (
                          <div className="border border-emerald-500/30 rounded-lg overflow-hidden">
                            <div className="divide-y divide-emerald-100">
                              {s.result.travaux_detail.map((t, ti) => (
                                <div key={ti} className="flex items-center justify-between px-4 py-2 text-xs">
                                  <span className="font-medium">{t.label}</span>
                                  <span className="text-muted-foreground">{t.qty} {t.unit} x {fmtC(t.unit_price)}</span>
                                  <span className="font-semibold">{fmtC(t.total)}</span>
                                </div>
                              ))}
                            </div>
                            <div className="border-t-2 border-emerald-500/40 px-4 py-2 flex items-center justify-between bg-emerald-500/10">
                              <span className="text-xs font-bold">CA TTC</span>
                              <span className="font-bold">{fmtC(s.result.caTTC)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {r.eligMPR.eligible && r.resultsMPR.length > 0 && (
                      <div className="p-4 border-b border-border/50">
                        <p className="text-xs font-bold text-blue-700 mb-2">MPR — Types</p>
                        {r.resultsMPR.map((m, mi) => (
                          <div key={mi} className="flex items-center justify-between text-xs py-1.5 border-b border-border/20 last:border-0">
                            <span className="font-medium">{m.type}</span>
                            {!hideFinancials && (
                            <div className="flex gap-4">
                              <span>Aides: {fmtC(m.result.totalAides)}</span>
                              <span className="text-emerald-600 font-bold">Marge: {fmtC(m.result.marge)}</span>
                            </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {!r.elig174.eligible && <div className="p-3"><p className="text-xs text-red-600">{r.elig174.reason}</p></div>}
                    {!r.eligMPR.eligible && <div className="p-3"><p className="text-xs text-amber-600">{r.eligMPR.reason}</p></div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* RDV FLOW MODAL — sequential per beneficiary with busy-slot filtering */}
        <Dialog open={showRdvFlow} onOpenChange={setShowRdvFlow}>
          <DialogContent className="rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Planifier RDV — {rdvCurrentIdx + 1}/{rdvData.length}
              </DialogTitle>
            </DialogHeader>
            {rdvData[rdvCurrentIdx] && (() => {
              const cur = rdvData[rdvCurrentIdx];
              const today = new Date().toISOString().split("T")[0];
              const allHours = Array.from({ length: 12 }, (_, i) => `${String(i + 9).padStart(2, "0")}:00`);
              const availableHours = allHours.filter(h => !rdvBusySlots.includes(h));
              const isWeekend = cur.date && (new Date(cur.date + "T12:00:00").getDay() === 0 || new Date(cur.date + "T12:00:00").getDay() === 6);

              // Fetch busy slots when date changes
              const fetchBusy = async (date) => {
                if (!date || !rdvAdminName) { setRdvBusySlots([]); return; }
                try {
                  const res = await fetch(`${API}/planning/busy-slots/${encodeURIComponent(rdvAdminName)}?date=${date}`);
                  const data = await res.json();
                  setRdvBusySlots(data.busy_slots || []);
                } catch (err) { setRdvBusySlots([]); }
              };

              return (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm">
                    <p className="font-bold">{cur.nom}</p>
                    <p className="text-xs text-muted-foreground">{cur.adresse} — {cur.programme}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">RDV avec <strong>{rdvAdminName}</strong></p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold">Date</label>
                      <input type="date" min={today} value={cur.date} onChange={e => {
                        const newDate = e.target.value;
                        setRdvData(prev => prev.map((r, i) => i === rdvCurrentIdx ? { ...r, date: newDate, heure: "" } : r));
                        fetchBusy(newDate);
                      }} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
                      {isWeekend && <p className="text-[10px] text-red-500 mt-1">Week-end : pas de RDV</p>}
                    </div>
                    <div>
                      <label className="text-xs font-semibold">Créneau</label>
                      {cur.date && !isWeekend ? (
                        availableHours.length > 0 ? (
                          <select value={cur.heure} onChange={e => setRdvData(prev => prev.map((r, i) => i === rdvCurrentIdx ? { ...r, heure: e.target.value } : r))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
                            <option value="">Choisir...</option>
                            {availableHours.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                        ) : (
                          <div className="w-full mt-1 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-600">Aucun créneau</div>
                        )
                      ) : (
                        <div className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-muted text-sm text-muted-foreground">Sélectionnez une date</div>
                      )}
                      {cur.date && !isWeekend && rdvBusySlots.length > 0 && (
                        <p className="text-[10px] text-amber-600 mt-1">{rdvBusySlots.length} créneau(x) occupé(s)</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={handleRdvSkip} className="flex-1 rounded-lg text-amber-600 border-amber-300">
                      <SkipForward className="w-4 h-4 mr-1" />Passer
                    </Button>
                    <Button onClick={handleRdvNext} disabled={!cur.date || !cur.heure || isWeekend}
                      className="flex-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white">
                      {rdvCurrentIdx < rdvData.length - 1 ? "Suivant" : (rdvSaving ? "Création..." : "Terminer et créer")}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground text-center">
                    "Passer" créera le dossier sans RDV pour ce bénéficiaire
                  </p>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return null;
}
